import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText, Users, Clock, CheckCircle } from "lucide-react";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalDevis: 0,
    pendingDevis: 0,
    treatedDevis: 0,
    totalContacts: 0,
  });
  const [recentDevis, setRecentDevis] = useState<any[]>([]);

  useEffect(() => {
    fetchStats();
    fetchRecentDevis();
  }, []);

  const fetchStats = async () => {
    const { data: quotes } = await supabase.from("quote_requests").select("*");
    if (quotes) {
      const uniqueEmails = new Set(quotes.map((q) => q.email));
      setStats({
        totalDevis: quotes.length,
        pendingDevis: quotes.filter((q) => q.status === "pending").length,
        treatedDevis: quotes.filter((q) => q.status !== "pending").length,
        totalContacts: uniqueEmails.size,
      });
    }
  };

  const fetchRecentDevis = async () => {
    const { data } = await supabase
      .from("quote_requests")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(5);
    if (data) setRecentDevis(data);
  };

  const statCards = [
    { label: "Total devis", value: stats.totalDevis, icon: FileText, color: "text-primary" },
    { label: "En attente", value: stats.pendingDevis, icon: Clock, color: "text-amber-500" },
    { label: "Traités", value: stats.treatedDevis, icon: CheckCircle, color: "text-emerald-500" },
    { label: "Contacts uniques", value: stats.totalContacts, icon: Users, color: "text-violet-500" },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <Card key={stat.label} className="border-0 shadow-sm">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                    <p className="text-3xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <stat.icon className={`w-10 h-10 ${stat.color} opacity-80`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Recent quotes */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Dernières demandes de devis</CardTitle>
          </CardHeader>
          <CardContent>
            {recentDevis.length === 0 ? (
              <p className="text-muted-foreground text-sm">Aucune demande pour le moment.</p>
            ) : (
              <div className="space-y-3">
                {recentDevis.map((devis) => (
                  <div
                    key={devis.id}
                    className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors"
                  >
                    <div>
                      <p className="font-medium text-sm">{devis.full_name}</p>
                      <p className="text-xs text-muted-foreground">{devis.email}</p>
                      {devis.company_name && (
                        <p className="text-xs text-muted-foreground">{devis.company_name}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          devis.status === "pending"
                            ? "bg-amber-100 text-amber-700"
                            : devis.status === "contacted"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-emerald-100 text-emerald-700"
                        }`}
                      >
                        {devis.status === "pending" ? "En attente" : devis.status === "contacted" ? "Contacté" : "Traité"}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(devis.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
